import { defineStore } from 'pinia'
import { makeInFlightRequestDeduper } from '@/utils/helpers/pinia'

/**
 * @typedef {{ type: string, key: string, position: number }} NavItem
 */

// De-dupe concurrent nav fetches for the same org: the org switch fires this imperatively right
// after the token refresh and the CyMainNav watcher fires it again when orgCanonical changes;
// without this guard that is two /nav requests per switch (ENG-361).
const dedupeNavFetch = makeInFlightRequestDeduper()

export const useNavConfigStore = defineStore('navConfig', {
  state: () => ({
    /** @type {NavItem[]} */
    positions: [],
    /** @type {string|null} org the most recent fetch targets, to drop stale cross-org responses */
    _latestOrgCanonical: null,
  }),

  getters: {
    /**
     * @param {object} state
     * @returns {(type: string, key: string) => number}
     */
    positionFor: (state) => (type, key) => {
      const item = state.positions.find(
        (p) => p.type === type && p.key === String(key),
      )
      return item?.position ?? Infinity
    },
  },

  actions: {
    async FETCH_NAV_CONFIG ({ orgCanonical }) {
      // No target org (e.g. a child-scope switch with no nextCanonical): nothing to fetch, and
      // fetching for `undefined` would reset positions and blank the sidebar (ENG-361).
      if (!orgCanonical) return

      // dedupeNavFetch only collapses concurrent fetches for the *same* org; on a fast A->B switch a
      // slower org-A response can still land after org-B's. Record the org this fetch is for and drop
      // its response if a later fetch for a different org has since superseded it (ENG-361).
      this._latestOrgCanonical = orgCanonical

      return dedupeNavFetch(orgCanonical, async () => {
        try {
          const { data } = (await this.$cycloid.ydAPI.getOrgNav(orgCanonical)) || {}
          if (this._latestOrgCanonical !== orgCanonical) return
          this.positions = data?.items ?? []
        } catch (_err) {
          if (this._latestOrgCanonical !== orgCanonical) return
          this.positions = []
        }
      })
    },

    async SAVE_NAV_CONFIG ({ orgCanonical, items }) {
      const { data, errors } = (await this.$cycloid.ydAPI.putOrgNav(orgCanonical, { items })) || {}
      if (errors?.length) throw new Error(errors[0].message)
      this.positions = data?.items ?? []
    },
  },
})
